/*
 * Copyright (c) 2022 automata-network/witness-v2 authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { useCallback, useEffect, useState } from 'react'

import { lighten } from 'polished'
import styled, { css } from 'styled-components'

import { pxToVw } from 'src/functions/units'

import FlexBox from '../Common/FlexBox'

type Props = {
  checked?: boolean
  defaultChecked?: boolean
  disabled?: boolean
  onChange?: (checked: boolean) => void
}

const ToggleSwitch = ({
  checked = false,
  defaultChecked = false,
  disabled = false,
  onChange
}: Props) => {
  const [isInit, setIsInit] = useState<boolean>(true)
  const [isChecked, setIsChecked] = useState<boolean>(defaultChecked || checked)

  useEffect(() => {
    if (isInit) {
      setIsChecked(checked)
    }
  }, [checked, isInit])

  const onSwitchClick = useCallback(() => {
    if (disabled) return

    const _isChecked = !isChecked
    setIsInit(false)
    setIsChecked((prev) => !prev)

    onChange && onChange(_isChecked)
  }, [disabled, isChecked, onChange])

  return (
    <StyledSwitch onClick={onSwitchClick}>
      <StyledSwitchTrack alignItems="center" $isChecked={isChecked || false}>
        <StyledSwitchThumb $isChecked={isChecked || false} $isInit={isInit} />
      </StyledSwitchTrack>
    </StyledSwitch>
  )
}

export default ToggleSwitch

const StyledSwitch = styled.button`
  appearance: none;
  padding: 0;
  border: 0;
  background: none;
  height: ${pxToVw(26)};
  width: ${pxToVw(42)};
  cursor: pointer;

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      height: ${pxToVw(26, 720)};
      width: ${pxToVw(42, 720)};
    `}
  `}
`

const StyledSwitchTrack = styled(FlexBox)<{
  $isChecked: boolean
}>`
  height: 100%;
  width: 100%;
  background-color: ${({ theme, $isChecked }) =>
    $isChecked ? lighten(0.4, theme.colors.primary) : theme.colors.gray200};
  border: solid 1px
    ${({ theme, $isChecked }) => ($isChecked ? theme.colors.primary : theme.colors.gray)};
  border-radius: ${pxToVw(26 / 2)};
  padding: ${pxToVw(2)};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      border-radius: ${pxToVw(26 / 2, 720)};
      padding: ${pxToVw(2, 720)};
    `}
  `}
`

const StyledSwitchThumb = styled.i<{
  $isChecked: boolean
  $isInit: boolean
}>`
  width: ${pxToVw(20)};
  height: ${pxToVw(20)};
  border-radius: ${pxToVw(20 / 2)};
  background-color: ${({ theme, $isChecked }) =>
    $isChecked ? theme.colors.primary : theme.colors.gray};
  transform: translateX(
    ${({ $isChecked }) => ($isChecked ? `calc(100% - ${pxToVw(4)})` : pxToVw(1))}
  );
  transition: ${({ $isInit }) => !$isInit && 'transform 0.175s ease-out'};

  ${({ theme, $isChecked }) => css`
    ${theme.mediaQuery.mobile`
      width: ${pxToVw(20, 720)};
      height: ${pxToVw(20, 720)};
      border-radius: ${pxToVw(20 / 2, 720)};
      transform: translateX(
        ${$isChecked ? `calc(100% - ${pxToVw(4, 720)})` : pxToVw(1, 720)}
      );
    `}
  `}
`
